import React, { useState, useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import { Vendor } from '../../../types/vendor.ts';
import StoryCard from './StoryCard.tsx';

interface VendorShowcaseProps {
  vendors: Vendor[];
  selectedCategory: string;
  onSelect: (vendor: Vendor) => void;
  onFavoriteToggle?: (vendor: Vendor) => void;
  favoriteVendors?: Vendor[];
}

const VendorShowcase: React.FC<VendorShowcaseProps> = ({ 
  vendors, 
  selectedCategory, 
  onSelect,
  onFavoriteToggle,
  favoriteVendors = []
}) => {
  // Only log for Photography category
  if (selectedCategory === 'Photography') {
    console.log('Photography vendors:', vendors.map(v => ({
      name: v.name,
      type: v.vendorType,
      verified: v.verified,
      topVendor: v.topVendor
    })));
  }

  // Check specifically for WorldByPixels
  const worldByPixels = vendors.find(v => v.name === "WorldByPixels");
  console.log('WorldByPixels vendor check:', {
    found: !!worldByPixels,
    vendor: worldByPixels,
    selectedCategory,
    totalVendors: vendors.length
  });

  const [sliderRef, setSliderRef] = useState<HTMLDivElement | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  const autoScrollRef = useRef<number | null>(null);
  const scrollSpeed = useRef(1); // Base scroll speed in pixels per frame

  // Create an infinite array of vendors by repeating the array 3 times
  const infiniteVendors = [...vendors, ...vendors, ...vendors];
  
  // Check if WorldByPixels is in the infinite array
  console.log('WorldByPixels in infinite array:', {
    found: infiniteVendors.some(v => v.name === "WorldByPixels"),
    count: infiniteVendors.filter(v => v.name === "WorldByPixels").length
  });

  console.log('VendorShowcase vendors:', vendors.map(v => ({
    name: v.name,
    type: v.vendorType,
    category: selectedCategory
  })));

  useEffect(() => {
    const startAutoScroll = () => {
      if (!sliderRef || autoScrollRef.current) return;

      const animate = () => {
        sliderRef.scrollLeft += scrollSpeed.current;

        // Reset scroll position when reaching the end of the middle section
        if (sliderRef.scrollLeft >= (sliderRef.scrollWidth * 2) / 3) {
          sliderRef.scrollLeft = sliderRef.scrollWidth / 3;
        }

        autoScrollRef.current = requestAnimationFrame(animate);
      };

      autoScrollRef.current = requestAnimationFrame(animate);
    };

    const stopAutoScroll = () => {
      if (autoScrollRef.current) {
        cancelAnimationFrame(autoScrollRef.current);
        autoScrollRef.current = null;
      }
    };

    // Start auto-scroll if not hovered
    if (!isHovered && !isDragging) {
      startAutoScroll();
    } else {
      stopAutoScroll();
    }

    return () => stopAutoScroll();
  }, [isHovered, isDragging, sliderRef]);

  // Update scroll speed based on hover state
  useEffect(() => {
    scrollSpeed.current = isHovered ? 0.2 : 3; // Slow down on hover
  }, [isHovered]);

  const handleMouseDown = (e: React.MouseEvent) => {
    setIsDragging(true);
    setStartX(e.pageX - (sliderRef?.offsetLeft || 0));
    setScrollLeft(sliderRef?.scrollLeft || 0);
  };

  const handleMouseLeave = () => {
    setIsDragging(false);
    setIsHovered(false);
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging || !sliderRef) return;
    e.preventDefault();
    const x = e.pageX - (sliderRef.offsetLeft || 0);
    const walk = (x - startX) * 2;
    sliderRef.scrollLeft = scrollLeft - walk;
  };

  // Initialize scroll position to the start of the middle section
  useEffect(() => {
    if (sliderRef) {
      sliderRef.scrollLeft = sliderRef.scrollWidth / 3;
    }
  }, [sliderRef, vendors]); // Added vendors as dependency

  return (
    <div className="relative w-full overflow-visible">
      {/* Content wrapper with gradient background */}
      <div className="relative overflow-visible">
        {/* Gradient Background Container */}
        <div className="absolute inset-0 rounded-[40px] overflow-visible">
          {/* Main diagonal gradient background */}
          <div className="absolute inset-0 bg-gradient-to-br from-pink-50 via-pink-25 to-purple-25 opacity-70" />
          
          {/* Overlay gradients for depth */}
          <div className="absolute inset-0">
            {/* Top-left to bottom-right flow */}
            <div className="absolute -top-1/4 -left-1/4 w-[120%] h-[120%] bg-gradient-to-br from-[#FFB2B2]/20 via-transparent to-transparent rounded-full blur-[80px] animate-pulse-slow" />
            
            {/* Bottom-right purple glow */}
            <div className="absolute bottom-0 right-0 w-[90%] h-[90%] bg-gradient-to-tl from-[#C4C6FF]/20 via-[#C4C6FF]/10 to-transparent rounded-full blur-[100px] animate-pulse-slow"
              style={{ animationDelay: '2s' }}
            />

            {/* Top-right accent */}
            <div className="absolute -top-1/4 -right-1/4 w-[100%] h-[100%] bg-gradient-to-bl from-[#FFB8D2]/15 via-transparent to-transparent rounded-full blur-[120px] animate-pulse-slow"
              style={{ animationDelay: '1s' }}
            />

            {/* Bottom-left accent */}
            <div className="absolute -bottom-1/4 -left-1/4 w-[100%] h-[100%] bg-gradient-to-tr from-[#FFC4C4]/15 via-transparent to-transparent rounded-full blur-[120px] animate-pulse-slow"
              style={{ animationDelay: '0.5s' }}
            />
            
            {/* Floating accent elements */}
            <div className="absolute top-10 right-10 w-40 h-40 bg-gradient-to-br from-white/10 to-transparent rounded-full blur-3xl animate-float-slow" />
            <div className="absolute bottom-10 left-10 w-40 h-40 bg-gradient-to-tr from-white/10 to-transparent rounded-full blur-3xl animate-float-slow-reverse" />
          </div>
        </div>

        {/* Content */}
        <div className="relative z-10 p-10 backdrop-blur-[2px]">
          <motion.div
            key={selectedCategory}
            initial={{ opacity: 0, x: 100 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -100 }}
            transition={{ duration: 0.5, ease: "easeInOut" }}
          >
            <div className="relative mb-12">
              <h2 className="text-4xl font-medium text-[#2D3648]">
                Discover {selectedCategory}
                <div className="absolute -bottom-3 left-0 right-0 h-1.5 bg-gradient-to-r from-[#FF4E8E] via-[#FF69B4] to-[#FF4E8E] rounded-full opacity-80 shadow-lg"></div>
              </h2>
            </div>
            
            <div className="relative overflow-visible pt-4">
              <motion.div 
                ref={setSliderRef}
                className="flex flex-nowrap gap-6 pb-8 overflow-x-auto scrollbar-hide cursor-grab active:cursor-grabbing overflow-visible"
                style={{ 
                  WebkitOverflowScrolling: 'touch',
                  scrollBehavior: 'smooth',
                  width: '100%',
                  minWidth: '100%',
                  paddingLeft: '1rem',
                  paddingRight: '1rem'
                }}
                onMouseEnter={() => setIsHovered(true)}
                onMouseLeave={handleMouseLeave}
                onMouseDown={handleMouseDown}
                onMouseUp={handleMouseUp}
                onMouseMove={handleMouseMove}
              >
                {infiniteVendors.map((vendor, index) => {
                  // Log every 10th vendor to track rendering
                  if (index % 10 === 0) {
                    console.log('Rendering vendor batch:', {
                      startIndex: index,
                      vendors: infiniteVendors.slice(index, index + 10).map(v => v.name)
                    });
                  }
                  return (
                    <motion.div
                      key={`${vendor.id}-${index}`}
                      initial={{ opacity: 0, scale: 0.8 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ 
                        delay: index * 0.1,
                        type: "spring",
                        stiffness: 100,
                        damping: 15
                      }}
                      className="flex-none w-[280px] md:w-[320px] relative z-0 transition-all duration-300 ease-out hover:scale-[1.02] hover:z-10"
                      style={{ 
                        willChange: 'transform',
                        transform: `translateX(${isDragging ? '0px' : '0px'})`,
                        flexShrink: 0,
                        marginRight: '1.5rem'
                      }}
                    >
                      <StoryCard 
                        vendor={vendor} 
                        index={index} 
                        onSelect={() => !isDragging && onSelect(vendor)}
                        onFavoriteToggle={() => onFavoriteToggle?.(vendor)}
                        isFavorite={favoriteVendors.some(v => v.id === vendor.id)}
                      />
                    </motion.div>
                  );
                })}
              </motion.div>
            </div>
            
            {/* Scroll progress indicator */}
            {sliderRef && (
              <motion.div 
                className="mt-8 h-1.5 bg-gradient-to-r from-pink-400/60 via-purple-400/60 to-pink-400/60 rounded-full overflow-hidden mx-auto max-w-md backdrop-blur-sm shadow-lg"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.5 }}
              >
              </motion.div>
            )}
          </motion.div>
        </div>
      </div>

      <style>{`
        @keyframes pulse-slow {
          0%, 100% { opacity: 0.2; transform: scale(1); }
          50% { opacity: 0.3; transform: scale(1.05); }
        }
        @keyframes float-slow {
          0%, 100% { transform: translateY(0) scale(1); }
          50% { transform: translateY(-20px) scale(1.05); }
        }
        @keyframes float-slow-reverse {
          0%, 100% { transform: translateY(0) scale(1); }
          50% { transform: translateY(20px) scale(1.05); }
        }
      `}</style>
    </div>
  );
};

export default VendorShowcase; 